import { useState } from 'react';
import { usePortfolio } from '../hooks/usePortfolio';
import { useCoins } from '../hooks/useCoins';
import { useSettings } from '../hooks/useSettings';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { formatPrice, formatNumber, getChangeClass } from '../lib/utils';
import { Plus, Trash2, DollarSign, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';

// Force HMR update on file change
if (import.meta.hot) {
  import.meta.hot.accept();
}

export default function Portfolio() {
  const { settings } = useSettings();
  const { portfolio, addToPortfolio, removeFromPortfolio } = usePortfolio();
  const { coins } = useCoins(settings.currency);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [coinToDelete, setCoinToDelete] = useState<string | null>(null);
  const [selectedCoin, setSelectedCoin] = useState('');
  const [amount, setAmount] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');

  const portfolioWithPrices = portfolio.map((item) => {
    const coin = coins.find((c) => c.id === item.coinId);
    return {
      ...item,
      currentPrice: coin?.current_price || 0,
      change24h: coin?.price_change_percentage_24h || 0,
      name: coin?.name || item.coinId,
      symbol: coin?.symbol || '',
      image: coin?.image || '',
    };
  });

  const totalValue = portfolioWithPrices.reduce(
    (sum, item) => sum + item.amount * item.currentPrice,
    0
  );

  const totalCost = portfolioWithPrices.reduce(
    (sum, item) => sum + item.amount * item.purchasePrice,
    0
  );

  const totalProfitLoss = totalValue - totalCost;
  const profitLossPercentage = totalCost > 0 ? (totalProfitLoss / totalCost) * 100 : 0;

  const handleAddToPortfolio = () => {
    if (!selectedCoin || !amount || !purchasePrice) return;

    addToPortfolio({
      coinId: selectedCoin,
      amount: parseFloat(amount),
      purchasePrice: parseFloat(purchasePrice),
      purchaseDate: new Date().toISOString(),
    });

    setSelectedCoin('');
    setAmount('');
    setPurchasePrice('');
    setShowAddModal(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">Portfolio</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Track your cryptocurrency investments
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4" />
          Add Asset
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <DollarSign className="h-5 w-5 text-black dark:text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Value</p>
                <p className="text-xl font-bold text-black dark:text-white">
                  {formatPrice(totalValue, settings.currency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Profit/Loss</p>
                <p className={`text-xl font-bold ${getChangeClass(totalProfitLoss)}`}>
                  {formatPrice(totalProfitLoss, settings.currency)}
                </p>
                <p className={`text-xs ${getChangeClass(profitLossPercentage)}`}>
                  {profitLossPercentage >= 0 ? '+' : ''}
                  {profitLossPercentage.toFixed(2)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <TrendingDown className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Cost</p>
                <p className="text-xl font-bold text-black dark:text-white">
                  {formatPrice(totalCost, settings.currency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h3 className="font-semibold text-black dark:text-white">Assets</h3>
        </CardHeader>
        <CardContent>
          {portfolioWithPrices.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p className="mb-4">No assets in your portfolio yet.</p>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4" />
                Add Your First Asset
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {portfolioWithPrices.map((item) => (
                <div
                  key={item.coinId}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="h-8 w-8 rounded-full" />
                    )}
                    <div>
                      <p className="font-medium text-black dark:text-white">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                        {item.symbol}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Amount</p>
                      <p className="font-medium text-black dark:text-white">
                        {item.amount.toFixed(4)}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Current</p>
                      <p className="font-medium text-black dark:text-white">
                        {formatPrice(item.currentPrice, settings.currency)}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Value</p>
                      <p className="font-medium text-black dark:text-white">
                        {formatPrice(item.amount * item.currentPrice, settings.currency)}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">24h</p>
                      <Badge
                        variant={item.change24h > 0 ? 'success' : 'destructive'}
                        className="text-xs"
                      >
                        {item.change24h >= 0 ? '+' : ''}
                        {item.change24h.toFixed(2)}%
                      </Badge>
                    </div>

                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setCoinToDelete(item.coinId);
                        setShowDeleteModal(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <h3 className="font-semibold text-lg text-slate-900 dark:text-slate-100">
                Add Asset
              </h3>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Select Coin
                </label>
                <select
                  value={selectedCoin}
                  onChange={(e) => setSelectedCoin(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">Select a coin</option>
                  {coins.map((coin) => (
                    <option key={coin.id} value={coin.id}>
                      {coin.name} ({coin.symbol.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Purchase Price ({settings.currency.toUpperCase()})
                </label>
                <input
                  type="number"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleAddToPortfolio}
                  disabled={!selectedCoin || !amount || !purchasePrice}
                >
                  Add Asset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showDeleteModal && coinToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 dark:text-slate-100">
                  Confirm Delete
                </h3>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600 dark:text-slate-400">
                Are you sure you want to delete this asset from your portfolio? This action cannot be undone.
              </p>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCoinToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    if (coinToDelete) {
                      removeFromPortfolio(coinToDelete);
                    }
                    setShowDeleteModal(false);
                    setCoinToDelete(null);
                  }}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}